import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { PreferenciasService } from './services/preferencias.service';
import { FirebaseMessagingService } from './core/services/fcm.service';
import { SpinnerService } from './core/services/spinner.service';
import { ThemeColorService } from '@app/core/services/theme-color.service';
import { AuthService } from '@app/core/auth/auth.service';
import { PdfLoadingService } from '@app/core/services/pdf-loading.service';
import { AppUpdateService } from '@app/core/services/app-update.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    readonly updates = inject(AppUpdateService);
    temaActual: 'light' | 'dark' = 'light';
    isLoading = false;

    private readonly spinner       = inject(SpinnerService);
    private readonly themeColor    = inject(ThemeColorService);
    private readonly auth          = inject(AuthService);
    private readonly router        = inject(Router);
    readonly pdfLoading            = inject(PdfLoadingService);
    readonly isPdfLoading$         = this.pdfLoading.visible$;

    // Colores de la barra (ajustá a tu palette real)
    private readonly LIGHT_BAR = '#f8fafc';
    private readonly DARK_BAR = '#27272a';

    // Subs
    private spinnerSub?: Subscription;
    private routerSub?: Subscription;

    // --- detección iOS/PWA ---
    private readonly isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);
    private readonly isStandalone = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;

    // handlers para poder desuscribir en ngOnDestroy
    private onVisibilityChange = () => {
        if (!document.hidden) {
            this.auth.onAppResume();
            this.setVHVar();
            this.scheduleHardReset(); // por si desde background quedó algo colgado
        }
    };

    private onPageShow = (e: PageTransitionEvent) => {
        // e.persisted === true cuando volvés desde bfcache (iOS/Safari)
        if ((e as any).persisted) {
            this.auth.onAppResume();
            this.setVHVar();
            this.scheduleHardReset();
        }
    };

    private readonly prefs = inject(PreferenciasService);
    private readonly fcmService = inject(FirebaseMessagingService);

    async ngOnInit() {
        this.updates.start();
        // 1) Fix de 100vh
        this.setVHVar();
        window.addEventListener('resize', this.setVHVar as any, { passive: true });

        // 1.1) Rehidratar al volver del background / bfcache
        document.addEventListener('visibilitychange', this.onVisibilityChange as any, { passive: true } as any);
        window.addEventListener('pageshow', this.onPageShow as any, { passive: true });

        // 1.2) Hard reset muy temprano
        this.scheduleHardReset();

        // 1.3) Tras cada navegación: reset + priming (solo primera vez)
        this.routerSub = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
            // Recalcular --vh tras navegaciones
            this.setVHVar();
            // Evitar hard reset agresivo en cada navegación (causaba glitch)
            // this.scheduleHardReset();
            // Desactivar el priming silencioso de layout para evitar flicker inicial
            // this.primeLayoutOnce();
        });

        // 2) Tema
        this.temaActual = this.prefs.obtenerTema() ?? 'light';
        this.applyTheme(this.temaActual);

        // 3) FCM
        this.fcmService.requestPermissionAndGetToken().then((token) => {
            if (token) console.info('Token FCM registrado:', token);
        });

        // 4) Spinner
        this.spinnerSub = this.spinner.isLoading.subscribe((isLoading) => (this.isLoading = isLoading));
    }

    ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.onVisibilityChange as any);
        window.removeEventListener('pageshow', this.onPageShow as any);
        window.removeEventListener('resize', this.setVHVar as any);

        this.routerSub?.unsubscribe();
        this.spinnerSub?.unsubscribe();
    }

    /** Ajusta --vh para iOS/Android */
    private setVHVar = () => {
        const vh = (window.visualViewport?.height ?? window.innerHeight) * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    /** Programa varias pasadas del hard reset para cubrir oleadas de inicialización de WebKit */
    private scheduleHardReset() {
        // Solo castigamos en iOS o en PWA standalone
        if (!this.isIOS && !this.isStandalone) {
            // como fallback, igual limpiamos locks “suaves”
            setTimeout(() => this.unlockScroll(), 0);
            return;
        }

        const runs = [0, 120]; // menos pasadas para reducir parpadeos
        runs.forEach((ms) => setTimeout(() => this.hardResetLayout(), ms));
    }

    /** Desbloquea scroll si quedó alguna clase/estilo colgado (overlays/menú móvil/PrimeNG) */
    private unlockScroll() {
        if (this.updates.required() || this.hasOpenDialog()) return;
        const badBody = ['blocked-scroll', 'layout-mobile-active', 'overflow-hidden', 'p-overflow-hidden'];
        const badHtml = ['overflow-hidden'];

        badBody.forEach((c) => document.body.classList.remove(c));
        badHtml.forEach((c) => document.documentElement.classList.remove(c));

        // Por si algún overlay dejó inline styles
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // iOS scroll suave
        (document.documentElement as any).style.webkitOverflowScrolling = 'touch';

        // PrimeNG OverlayScrollBlock puede dejar padding-right
        document.body.style.paddingRight = '';
    }

    /** Limpia máscaras/overlays y fuerza reflow/scroll para recomponer layout en iOS */
    private hardResetLayout() {
        if (this.updates.required() || this.hasOpenDialog()) return;
        this.unlockScroll();

        // Eliminar máscaras/overlays residuales de Prime
        document.querySelectorAll('.p-component-overlay, .p-dialog-mask, .p-sidebar-mask, .p-overlay, .p-menu-overlay, .p-confirm-dialog-mask').forEach((el) => el.remove());

        // Asegurar sidebar oculto al inicio (sin tocar tu servicio)
        const sidebar = document.querySelector<HTMLElement>('.layout-sidebar');
        if (sidebar) {
            sidebar.style.transform = 'translateX(-100%)';
            sidebar.style.left = '0';
            sidebar.style.top = '0';
        }

        // Evitar micro-scroll, gatillaba flashes en algunos casos

        // Pedir recálculo del offset del topbar (tu Topbar recalcula --topbar-offset en resize)
        window.dispatchEvent(new Event('resize'));
    }

    private hasOpenDialog(): boolean {
        return Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]'))
            .some(dialog => dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden');
    }

    /**
     * PRIMERA VEZ SOLO: simula “Cambiar sistema” en silencio.
     * Navega a /selector sin tocar la barra del navegador, y vuelve a la URL original.
     * Esto deja el layout en el mismo estado “bueno” que cuando lo hacés a mano.
     */
    private primeLayoutOnce() {
        if (sessionStorage.getItem('layoutPrimed') === '1') return;

        const currentUrl = this.router.url;
        // Evitar loop si ya estás en /selector
        if (currentUrl.startsWith('/selector')) {
            sessionStorage.setItem('layoutPrimed', '1');
            return;
        }

        sessionStorage.setItem('layoutPrimed', '1');

        // Hacemos el soft-reroute (sin ensuciar el historial ni la barra)
        this.router.navigateByUrl('/selector', { skipLocationChange: true }).then(() => this.router.navigateByUrl(currentUrl, { replaceUrl: true }));
    }

    toggleTheme() {
        this.temaActual = this.temaActual === 'light' ? 'dark' : 'light';
        this.prefs.guardarTema(this.temaActual);
        this.applyTheme(this.temaActual);
        // recalcular y sanear por si cambió algo visual
        this.scheduleHardReset();
    }

    /** Sincroniza Tailwind, PrimeNG, barra del navegador y status bar nativa */
    private async applyTheme(theme: 'light' | 'dark') {
        const isDark = theme === 'dark';
        const body = document.body;
        const html = document.documentElement;

        // Tailwind
        body.classList.toggle('dark', isDark);
        html.classList.toggle('dark', isDark);

        // PrimeNG tokens
        body.classList.toggle('p-theme-dark', isDark);
        body.classList.toggle('p-theme-light', !isDark);

        // Alias propios: en html y body
        const toggleDarkAlias = (el: HTMLElement, dark: boolean) => {
            el.classList.toggle('app-dark', dark);
        };
        toggleDarkAlias(document.documentElement, isDark);
        toggleDarkAlias(document.body, isDark);

        // Barra del navegador (PWA)
        this.themeColor.set(isDark ? this.DARK_BAR : this.LIGHT_BAR);
        // Overlay bajo la status bar (PWA iOS)
        this.themeColor.setAppleStatusBar('black-translucent');
    }
}
